import { definePattern, definePreset } from '@pandacss/dev';

import varnishTokens from '@allenai/varnish-theme/panda-theme-beta';

import { commonKeyframes } from './components/animations/common.animation';

// Panda complains about the unit coming from `varnish-theme` not matching the other values
// Varnish maps `0unit` => `0`, we are just adding the unit back here,
// in this case: `rem` so that it matches the other break point units.
Object.assign(varnishTokens.breakpoints, {
    xs: '0rem', // 0 with units
});

Object.assign(varnishTokens.tokens, {
    spacing: {
        '0': { value: '0' },
        '1': { value: '.25rem' },
        '2': { value: '.5rem' },
        '3': { value: '.75rem' },
        '4': { value: '1rem' },
        '5': { value: '1.25rem' },
        '6': { value: '1.5rem' },
        '7': { value: '1.75rem' },
        '8': { value: '2rem' },
    },
    zIndex: {
        backdrop: { value: '1300' },
        drawer: { value: '1400' },
        snackbar: { value: '1500' },
        modal: { value: '1600' },
    },
    fontSizes: {
        ...varnishTokens.tokens.fontSizes,
        icon: varnishTokens.tokens.sizes.icon, // we use font-size internally for icons
    },
});

// Just testing overriding a pattern
const stack = definePattern({
    properties: {
        align: { type: 'property', value: 'alignItems' },
        justify: { type: 'property', value: 'justifyContent' },
        direction: { type: 'property', value: 'flexDirection' },
        spacing: { type: 'token', value: 'spacing', property: 'gap' },
    },
    defaultValues: {
        direction: 'column',
        spacing: '1px',
    },
    transform(props) {
        const { align, justify, direction, spacing, ...rest } = props;
        return {
            display: 'flex',
            flexDirection: direction,
            alignItems: align,
            justifyContent: justify,
            gap: spacing,
            ...rest,
        };
    },
});

/**
 *  Create panda preset for consumption
 */
const selectors = {
    active: ':active, [data-active], [data-state="active"]',
    checked: ':checked, [data-checked], [aria-checked=true], [data-state="checked"]',
    disabled: ':disabled, [disabled], [data-disabled], [data-state="disabled"]',
    hover: ':hover, [data-hover], [data-hovered]',
    focus: ':focus, [data-focus], [data-focused=true]',
    invalid: '[aria-invalid=true], [data-invalid], [data-state="invalid"]',
    selected: '[aria-selected=true], [data-selected], [data-state="selected"]',
    dragging: '[data-dragging]',
    open: '[open], [data-open]',
    // this is the default Panda and expected readOnly selector, which we are including for completeness
    readOnly: ':read-only, [data-readonly]',
    // browser native CSS `:read-only` is always true for elements like `<div>`s, so for component level use
    // we are including a `componentReadOnly` that is just the `[data-readonly]` selector, which gets set
    // on the component.
    componentReadOnly: '[data-readonly]',
};

const preset = definePreset({
    name: 'varnish-preset',
    theme: {
        ...varnishTokens,
        keyframes: commonKeyframes,
        textStyles: {
            inherit: {
                description: 'Inherit text styles, useful on form controls',
                value: {
                    font: 'inherit',
                    lineHeight: 'inherit',
                    letterSpacing: 'inherit',
                    textDecoration: 'inherit',
                    textTransform: 'inherit',
                },
            },
        },
        layerStyles: {
            controlTransparent: {
                description: 'Sets input styles for controls that are wrapped with style container',
                value: {
                    color: 'inherit',
                    backgroundColor: 'transparent',
                    border: '0',
                    padding: '0',
                },
            },
        },
    },
    conditions: {
        light: '&.light, .light &, [data-color-mode=light] &, html[data-theme=light] &',
        dark: '&.dark, .dark &, [data-color-mode=dark] &, html[data-theme=dark] &',
        extend: {
            // we should try to use this mimimally as it will be hard to override
            // than relying on font-size (which can be set higher and inherited)
            icon: '& svg', // this is in newest panda

            notDisabled: `&:not(${selectors.disabled})`,
            notInvalid: `&:not(${selectors.invalid})`,

            // Should read as slot is(SELECTED) and not(DISABLED or INVALID)
            selectedNotDisabledNotInvalid: `&:is(${selectors.selected}):not(${selectors.disabled},${selectors.invalid})`,

            // These are extended versions of panda's default conditions
            active: `&:is(${selectors.active})`,
            checked: `&:is(${selectors.checked})`,
            disabled: `&:is(${selectors.disabled})`,
            hover: `&:is(${selectors.hover})`,
            focus: `&:is(${selectors.focus})`,
            hoverOrFocus: `&:is(${selectors.hover}), &:is(${selectors.focus})`,
            invalid: `&:is(${selectors.invalid})`,
            selected: `&:is(${selectors.selected})`,
            dragging: `&:is(${selectors.dragging})`,
            open: `&:is(${selectors.open})`,
            readOnly: `&:is(${selectors.readOnly})`,
            componentReadOnly: `&:is(${selectors.componentReadOnly})`,

            href: '&[data-href]',

            // non-group hoverNotDisabledNotSelected can be made with nesting the conditions

            groupActive: `.group:is(${selectors.active}) &`,
            groupChecked: `.group:is(${selectors.checked}) &`,
            groupDisabled: `.group:is(${selectors.disabled}) &`,
            groupHover: `.group:is(${selectors.hover}) &`,
            groupInvalid: `.group:is(${selectors.invalid}) &`,
            groupSelected: `.group:is(${selectors.selected}) &`,
            groupReadOnly: `.group:is(${selectors.readOnly}) &`,
            groupDragging: `.group:is(${selectors.dragging}) &`,
            groupOpen: `.group:is(${selectors.open}) &`,
            groupComponentReadOnly: `.group:is(${selectors.componentReadOnly}) &`,

            // these conditions are a bit long winded, but provide a single selector for the state
            // so that we don't end up with nested styles (harder to override).
            //
            // This should read as: `.group` is(HOVERED), but is not(SELECTED or DISABLED)
            groupHoverNotDisabledNotSelected: `.group:is(${selectors.hover}):not(${selectors.disabled},${selectors.selected}) &`,
            // This should read as: `.group` is(HOVERED), but is not(SELECTED or DISABLED or INVALID)
            groupHoverNotDisabledNotSelectedNotInvalid: `.group:is(${selectors.hover}):not(${selectors.disabled},${selectors.selected},${selectors.invalid}) &`,
        },
    },
    patterns: {
        stack,
    },
    utilities: {},
    globalCss: {
        '.varnish-markdown': {
            // TODO: Implement new markdown styles
            // import these styles from elsewhere?
            color: 'text', // needed?

            h1: {
                fontSize: '[2.25rem]',
                fontFamily: '[PP Telegraf]',
                fontWeight: '[500]',
                fontStyle: 'normal',
                letterSpacing: '0',
                lineHeight: '[43.2px]',
            },
            h2: {
                fontSize: '[2rem]',
                fontFamily: '[PP Telegraf]',
                fontStyle: 'normal',
                fontWeight: '[500]',
                lineHeight: '[38.4px]',
                letterSpacing: '0',
            },
            h3: {
                fontSize: '[1.5rem]',
                fontFamily: '[PP Telegraf]',

                fontStyle: 'normal',
                fontWeight: '[500]',
                lineHeight: '[28.8px]',
                letterSpacing: '0',
            },
            h4: {
                fontSize: '[1.25rem]',
                fontFamily: '[PP Telegraf]',
                fontStyle: 'normal',
                fontWeight: '[500]',
                lineHeight: '[24px]',
                letterSpacing: '0',
            },
            h5: {
                fontSize: '[1.125rem]',
                fontFamily: '[PP Telegraf]',

                fontStyle: 'normal',
                fontWeight: '[500]',
                lineHeight: '[21.6px]',
                letterSpacing: '0',
            },
            h6: {
                fontSize: '[1rem]',
                fontFamily: '[PP Telegraf]',
                fontStyle: 'normal',
                fontWeight: '[500]',
                lineHeight: '[20.8px]',
                letterSpacing: '0',
            },
            p: {
                fontSize: '[0.875rem]',
                fontFamily: '[Manrope]',
                fontStyle: 'normal',
                fontWeight: '[400]',
                lineHeight: '[21px]',
                letterSpacing: '0',
            },
            ul: {
                listStyleType: 'revert',
                marginBottom: '[16px]',
            },
            li: {
                marginBottom: '0',
            },
            img: {
                maxWidth: '[100%]',
                '&[src*="#thumbnail"]': {
                    width: '[64px]',
                },
            },
            em: {
                fontStyle: 'italic',
            },
            del: {
                textDecoration: 'line-through',
            },
            ol: {
                marginBottom: '[16px]',
            },
            hr: {
                borderColor: 'gray.60',
            },
            // Table
            table: {
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                fontSize: '17px',
            },
            thead: {
                // ...
            },
            tbody: {
                // ...
            },
            tfoot: {
                // ...
            },
            tr: {
                // ...
            },
            'th, td': {
                lineHeight: '26px',
                padding: '10px',
                borderBottom: '1px solid',
                borderBottomColor: 'elements.disabled.stroke/50', // not tokenized
                textWrapMode: 'nowrap',
                _first: {
                    paddingLeft: 0,
                },
                _last: {
                    paddingRight: 0,
                },
            },
            th: {},
            td: {
                // ...
            },
            caption: {
                // does this belong in top scope, or is it more specific?
                fontSize: '14px', // should this be set in both of the below?
                // directly before table
                ':has(+ table)': {
                    captionSide: 'top',
                    marginBottom: '13px',
                },
                // directly after table
                'table + &': {
                    captionSide: 'bottom',
                    marginTop: '13px',
                },
            },
        },
    },
});

export default preset;
